import { useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Wrench, Calendar, AlertTriangle, Gauge, FileText } from 'lucide-react';
import { useApp } from '@/store';
import { useT, type I18nKey } from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ResponsiveGrid, PageHeader } from '@/components/layout';
import { apiFetch } from '@/lib/api';
import { changes, type BaseDoc } from '@/lib/store-adapter';

enum ReportType {
  CUSTOMER_SUMMARY = 'customer_summary',
  EXTINGUISHER_DUE = 'extinguisher_due',
  TENANT_ACTIVITY = 'tenant_activity',
}

enum ReportFormat {
  PDF = 'pdf',
  CSV = 'csv',
}

enum ReportStatus {
  IDLE = 'idle',
  QUEUED = 'queued',
  DONE = 'done',
  ERROR = 'error',
}

interface ReportDoc extends BaseDoc {
  source?: string;
  status?: string;
  s3_url?: string;
}

function GenerateReportSection() {
  const t = useT();
  const typeId = useId();
  const formatId = useId();
  const [rtype, setRtype] = useState<ReportType>(ReportType.CUSTOMER_SUMMARY);
  const [format, setFormat] = useState<ReportFormat>(ReportFormat.PDF);
  const [status, setStatus] = useState<ReportStatus>(ReportStatus.IDLE);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Listen for the final `report` doc once we have a requestId.
  useEffect(() => {
    if (!requestId) return;
    const stop = changes(['report'], change => {
      const doc = change.doc as ReportDoc | undefined;
      if (!doc || doc.type !== 'report') return;
      if (doc.source !== requestId) return;
      if (doc.status === 'done' && doc.s3_url) {
        setStatus(ReportStatus.DONE);
        setDownloadUrl(doc.s3_url);
      } else if (doc.status === 'failed' || doc.status === 'error') {
        setStatus(ReportStatus.ERROR);
        setErrorMsg(t('reports.status.error'));
      }
    });
    return stop;
  }, [requestId, t]);

  const generate = async () => {
    setSubmitting(true);
    setDownloadUrl(null);
    setErrorMsg(null);
    setRequestId(null);
    setStatus(ReportStatus.QUEUED);
    try {
      const resp = await apiFetch<{ requestId: string }>(`/reports/${rtype}`, {
        method: 'POST',
        body: JSON.stringify({ format }),
      });
      setRequestId(resp.requestId);
    } catch (err) {
      setStatus(ReportStatus.ERROR);
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const types: ReportType[] = [
    ReportType.CUSTOMER_SUMMARY,
    ReportType.EXTINGUISHER_DUE,
    ReportType.TENANT_ACTIVITY,
  ];
  const formats: ReportFormat[] = [ReportFormat.PDF, ReportFormat.CSV];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <CardTitle>{t('reports.generate')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-3 sm:items-end">
          <div className="space-y-1">
            <Label htmlFor={typeId}>{t('reports.reportType')}</Label>
            <select
              id={typeId}
              value={rtype}
              onChange={e => setRtype(e.target.value as ReportType)}
              className="flex h-11 sm:h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-base sm:text-sm shadow-sm"
            >
              {types.map(v => (
                <option key={v} value={v}>
                  {t(`reports.type.${v}` as I18nKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor={formatId}>{t('reports.format')}</Label>
            <select
              id={formatId}
              value={format}
              onChange={e => setFormat(e.target.value as ReportFormat)}
              className="flex h-11 sm:h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-base sm:text-sm shadow-sm"
            >
              {formats.map(v => (
                <option key={v} value={v}>
                  {v.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={generate} disabled={submitting}>
            {t('reports.generateButton')}
          </Button>
        </div>
        {status !== ReportStatus.IDLE && (
          <div className="mt-3 flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">
              {status === ReportStatus.QUEUED && t('reports.status.queued')}
              {status === ReportStatus.DONE && t('reports.status.done')}
              {status === ReportStatus.ERROR &&
                (errorMsg ?? t('reports.status.error'))}
            </span>
            {downloadUrl && (
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {t('reports.download')}
              </a>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function Reports() {
  const t = useT();
  const { customers, selectedCustomerIds, settings, deviceTypes } = useApp();
  const selected = useMemo(() => {
    if (selectedCustomerIds.size === 0) return customers;
    return customers.filter(c => selectedCustomerIds.has(c.id));
  }, [customers, selectedCustomerIds]);

  const spareParts = useMemo(() => {
    const agg: Record<string, number> = {};
    selected.forEach(c => {
      c.extinguishers.forEach(e =>
        e.spareParts?.forEach(p => {
          agg[p] = (agg[p] ?? 0) + 1;
        })
      );
      c.hoses.forEach(h =>
        h.spareParts?.forEach(p => {
          agg[p] = (agg[p] ?? 0) + 1;
        })
      );
    });
    return Object.entries(agg);
  }, [selected]);

  const upcoming = useMemo(() => {
    const now = new Date();
    const horizon = new Date(now.getTime() + 90 * 86400000);
    return selected.filter(
      c => c.nextInspection && new Date(c.nextInspection) <= horizon
    );
  }, [selected]);

  const ageWarnings = useMemo(() => {
    const out: {
      customerId: string;
      ext: string;
      year: number;
      limit: number;
    }[] = [];
    selected.forEach(c => {
      c.extinguishers.forEach(e => {
        if (!e.year) return;
        const age = new Date().getFullYear() - e.year;
        const dt = deviceTypes.find(x => x.id === e.type);
        const limit = dt ? dt.ageLimit : settings.boostAgeLimit;
        if (age > limit)
          out.push({
            customerId: c.id,
            ext: `${e.mfr} ${e.model} #${e.deviceNumber}`,
            year: e.year,
            limit,
          });
      });
    });
    return out;
  }, [selected, settings, deviceTypes]);

  const pressureDue = useMemo(() => {
    const cutoff = new Date(Date.now() - 5 * 365 * 86400000);
    const out: { customerId: string; hose: string; last?: string }[] = [];
    selected.forEach(c =>
      c.hoses.forEach(h => {
        if (!h.pressureTestDate || new Date(h.pressureTestDate) < cutoff) {
          out.push({
            customerId: c.id,
            hose: `${h.mfr} ${h.model}`,
            last: h.pressureTestDate,
          });
        }
      })
    );
    return out;
  }, [selected]);

  const cards = [
    {
      icon: Wrench,
      title: t('reports.sparePartsSummary'),
      desc: `${spareParts.length} unique parts across ${selected.length} customers`,
      body: (
        <ul className="space-y-1 text-sm">
          {spareParts.slice(0, 10).map(([k, v]) => (
            <li key={k} className="flex justify-between">
              <span className="font-mono text-xs">{k}</span>
              <span>{v}×</span>
            </li>
          ))}
          {spareParts.length === 0 && (
            <li className="text-muted-foreground">
              {t('reports.noPartsUsed')}
            </li>
          )}
        </ul>
      ),
    },
    {
      icon: Calendar,
      title: t('reports.upcomingInspections'),
      desc: `${upcoming.length} due`,
      body: (
        <ul className="space-y-1 text-sm">
          {upcoming.slice(0, 8).map(c => (
            <li key={c.id}>
              <Link
                to={`/customer/${c.id}`}
                className="text-primary hover:underline"
              >
                {c.name}
              </Link>{' '}
              <span className="text-muted-foreground">
                — {c.nextInspection}
              </span>
            </li>
          ))}
          {upcoming.length === 0 && (
            <li className="text-muted-foreground">{t('reports.none')}</li>
          )}
        </ul>
      ),
    },
    {
      icon: AlertTriangle,
      title: t('reports.ageWarnings'),
      desc: `${ageWarnings.length} devices over limit`,
      body: (
        <ul className="space-y-1 text-sm">
          {ageWarnings.slice(0, 8).map((a, i) => (
            <li key={i}>
              <Link
                to={`/customer/${a.customerId}`}
                className="text-primary hover:underline"
              >
                {a.customerId}
              </Link>{' '}
              <span className="text-muted-foreground">
                — {a.ext} ({a.year})
              </span>
            </li>
          ))}
          {ageWarnings.length === 0 && (
            <li className="text-muted-foreground">{t('reports.noWarnings')}</li>
          )}
        </ul>
      ),
    },
    {
      icon: Gauge,
      title: t('reports.pressureTestDue'),
      desc: `${t('reports.pressureTestSpec')} — ${pressureDue.length} ${t('reports.due')}`,
      body: (
        <ul className="space-y-1 text-sm">
          {pressureDue.slice(0, 8).map((a, i) => (
            <li key={i}>
              <Link
                to={`/customer/${a.customerId}`}
                className="text-primary hover:underline"
              >
                {a.customerId}
              </Link>{' '}
              <span className="text-muted-foreground">— {a.hose}</span>
            </li>
          ))}
          {pressureDue.length === 0 && (
            <li className="text-muted-foreground">{t('reports.allCurrent')}</li>
          )}
        </ul>
      ),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <PageHeader
        title={t('nav.reports')}
        subtitle={`Based on ${selectedCustomerIds.size || 'all'} customer${selectedCustomerIds.size === 1 ? '' : 's'}`}
      />
      <GenerateReportSection />
      <ResponsiveGrid cols={2}>
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle>{c.title}</CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        {c.desc}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>{c.body}</CardContent>
              </Card>
            </motion.div>
          );
        })}
      </ResponsiveGrid>
    </motion.div>
  );
}
